// ─── Block Type Definitions ──────────────────────────────────────────────────

export type BlockType = 'header' | 'text' | 'image' | 'button' | 'divider' | 'spacer' | 'social' | 'footer';

export interface EmailBlock {
  id: string;
  type: BlockType;
  props: Record<string, any>;
}

export const BLOCK_DEFS: { type: BlockType; label: string; icon: string; defaultProps: Record<string, any> }[] = [
  {
    type: 'header', label: 'Header', icon: 'Type',
    defaultProps: { text: 'Your Heading Here', fontSize: 28, color: '#1E293B', bgColor: '#6366F1', textColor: '#FFFFFF', alignment: 'center', padding: 32 },
  },
  {
    type: 'text', label: 'Text', icon: 'AlignLeft',
    defaultProps: { text: 'Write your message here. You can use {{lead.name}} for personalization.', fontSize: 15, color: '#374151', bold: false, italic: false, alignment: 'left', padding: 16 },
  },
  {
    type: 'image', label: 'Image', icon: 'Image',
    defaultProps: { url: 'https://placehold.co/600x200/6366F1/white?text=Your+Image', alt: 'Image', width: '100%', alignment: 'center', link: '', padding: 8 },
  },
  {
    type: 'button', label: 'Button', icon: 'MousePointer',
    defaultProps: { text: 'Click Here', url: '#', bgColor: '#6366F1', textColor: '#FFFFFF', borderRadius: 8, alignment: 'center', padding: 16 },
  },
  {
    type: 'divider', label: 'Divider', icon: 'Minus',
    defaultProps: { color: '#E2E8F0', thickness: 1, width: 100, padding: 16 },
  },
  {
    type: 'spacer', label: 'Spacer', icon: 'MoveVertical',
    defaultProps: { height: 24 },
  },
  {
    type: 'social', label: 'Social Links', icon: 'Share2',
    defaultProps: { facebook: '', twitter: '', linkedin: '', instagram: '', alignment: 'center', padding: 16 },
  },
  {
    type: 'footer', label: 'Footer', icon: 'FileText',
    defaultProps: { text: '© 2026 Your Company. All rights reserved.\nYou received this email because you signed up.', fontSize: 12, color: '#94A3B8', alignment: 'center', padding: 24 },
  },
];

export function createBlock(type: BlockType): EmailBlock {
  const def = BLOCK_DEFS.find(d => d.type === type)!;
  return { id: `block_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, type, props: { ...def.defaultProps } };
}

// ─── Gallery Templates ──────────────────────────────────────────────────────

export interface GalleryTemplate {
  name: string;
  category: string;
  subject: string;
  description: string;
  color: string;
  blocks: EmailBlock[];
}

let _id = 0;
const b = (type: BlockType, props: Record<string, any>): EmailBlock => ({
  id: `gallery_${++_id}`, type, props: { ...BLOCK_DEFS.find(d => d.type === type)!.defaultProps, ...props },
});

export const GALLERY_TEMPLATES: GalleryTemplate[] = [
  {
    name: 'Welcome', category: 'welcome', subject: 'Welcome to {{lead.name}}!', description: 'Greet new leads with a warm welcome', color: '#6366F1',
    blocks: [
      b('header', { text: 'Welcome!', bgColor: '#6366F1' }),
      b('text', { text: 'Hi {{lead.name}},\n\nWe\'re thrilled to have you on board! Our team is ready to help you achieve your goals.', fontSize: 15 }),
      b('button', { text: 'Get Started', url: '#', bgColor: '#6366F1' }),
      b('divider', {}),
      b('footer', { text: '© 2026 Your Company. All rights reserved.' }),
    ],
  },
  {
    name: 'Follow Up', category: 'followup', subject: 'Just checking in, {{lead.name}}', description: 'Follow up with leads who haven\'t responded', color: '#10B981',
    blocks: [
      b('header', { text: 'Just Checking In', bgColor: '#10B981' }),
      b('text', { text: 'Hi {{lead.name}},\n\nI wanted to follow up on our recent conversation. Do you have any questions I can help with?' }),
      b('button', { text: 'Schedule a Call', url: '#', bgColor: '#10B981' }),
      b('spacer', { height: 16 }),
      b('footer', { text: 'Feel free to reply to this email anytime.' }),
    ],
  },
  {
    name: 'Special Offer', category: 'offer', subject: 'Exclusive offer for you, {{lead.name}}!', description: 'Send promotional offers to leads', color: '#EF4444',
    blocks: [
      b('header', { text: 'Special Offer!', bgColor: '#EF4444' }),
      b('text', { text: 'Hi {{lead.name}},\n\nFor a limited time, we\'re offering an exclusive deal just for you. Don\'t miss out!', fontSize: 16 }),
      b('image', { url: 'https://placehold.co/600x250/EF4444/white?text=50%25+OFF', alt: 'Offer Banner' }),
      b('button', { text: 'Claim Your Offer', url: '#', bgColor: '#EF4444' }),
      b('text', { text: '*Terms and conditions apply. Offer valid until end of month.', fontSize: 12, color: '#94A3B8' }),
      b('footer', { text: '© 2026 Your Company. Unsubscribe' }),
    ],
  },
  {
    name: 'Thank You', category: 'thankyou', subject: 'Thank you, {{lead.name}}!', description: 'Express gratitude to leads or customers', color: '#F59E0B',
    blocks: [
      b('header', { text: 'Thank You!', bgColor: '#F59E0B', textColor: '#FFFFFF' }),
      b('text', { text: 'Hi {{lead.name}},\n\nThank you for your interest! We truly appreciate your time and look forward to working together.' }),
      b('divider', {}),
      b('text', { text: 'What\'s next?\n\n1. Our team will review your requirements\n2. We\'ll reach out within 24 hours\n3. We\'ll set up a personalized demo for you' }),
      b('button', { text: 'View Your Account', url: '#', bgColor: '#F59E0B', textColor: '#FFFFFF' }),
      b('footer', { text: 'Need help? Just reply to this email.' }),
    ],
  },
  {
    name: 'Event Invite', category: 'event', subject: 'You\'re Invited!', description: 'Invite leads to events or webinars', color: '#8B5CF6',
    blocks: [
      b('header', { text: 'You\'re Invited!', bgColor: '#8B5CF6' }),
      b('text', { text: 'Hi {{lead.name}},\n\nWe\'d love for you to join us at our upcoming event!', fontSize: 16 }),
      b('text', { text: 'Event: Product Demo Day\nDate: Friday, April 15, 2026\nTime: 2:00 PM - 3:30 PM\nLocation: Online (Zoom)', fontSize: 14, bold: true }),
      b('button', { text: 'RSVP Now', url: '#', bgColor: '#8B5CF6' }),
      b('footer', { text: 'Can\'t make it? Reply and we\'ll send you the recording.' }),
    ],
  },
  {
    name: 'Newsletter', category: 'newsletter', subject: 'Your Weekly Update', description: 'Regular newsletter with multiple sections', color: '#06B6D4',
    blocks: [
      b('header', { text: 'Weekly Newsletter', bgColor: '#06B6D4' }),
      b('text', { text: 'Hi {{lead.name}}, here\'s what\'s new this week:', fontSize: 15 }),
      b('divider', {}),
      b('text', { text: 'New Feature Launch\n\nWe\'ve just released our latest feature that will help you manage leads 3x faster. Check it out!', bold: false }),
      b('divider', {}),
      b('text', { text: 'Tip of the Week\n\nDid you know you can use tags to organize your leads? Try it today!' }),
      b('button', { text: 'Read More', url: '#', bgColor: '#06B6D4' }),
      b('social', { facebook: '#', twitter: '#', linkedin: '#', instagram: '#' }),
      b('footer', { text: '© 2026 Your Company | Unsubscribe' }),
    ],
  },
  {
    name: 'Appointment Reminder', category: 'reminder', subject: 'Reminder: Your appointment is tomorrow', description: 'Remind leads about upcoming appointments', color: '#EC4899',
    blocks: [
      b('header', { text: 'Appointment Reminder', bgColor: '#EC4899' }),
      b('text', { text: 'Hi {{lead.name}},\n\nThis is a friendly reminder about your upcoming appointment.', fontSize: 15 }),
      b('text', { text: 'Date: Tomorrow\nTime: 10:00 AM\nDuration: 30 minutes\nLocation: Online', fontSize: 14, bold: true }),
      b('button', { text: 'Join Meeting', url: '#', bgColor: '#EC4899' }),
      b('text', { text: 'Need to reschedule? Click below:', fontSize: 13, color: '#64748B' }),
      b('button', { text: 'Reschedule', url: '#', bgColor: '#FFFFFF', textColor: '#EC4899' }),
      b('footer', { text: 'Questions? Reply to this email.' }),
    ],
  },
  {
    name: 'Re-engagement', category: 'reengagement', subject: 'We miss you, {{lead.name}}!', description: 'Re-engage inactive leads', color: '#F97316',
    blocks: [
      b('header', { text: 'We Miss You!', bgColor: '#F97316' }),
      b('text', { text: 'Hi {{lead.name}},\n\nIt\'s been a while since we last heard from you. We\'d love to reconnect!', fontSize: 15 }),
      b('text', { text: 'Here\'s a special incentive to come back — 20% off your next purchase!', fontSize: 16, bold: true, color: '#F97316' }),
      b('button', { text: 'Come Back & Save 20%', url: '#', bgColor: '#F97316' }),
      b('divider', {}),
      b('footer', { text: 'If you no longer wish to hear from us, click unsubscribe.' }),
    ],
  },
];

// ─── Block -> HTML (for previews & send) ────────────────────────────────
//
// Client-side mirror of backend email-renderer.ts. Used in:
//   - EmailBuilder canvas preview (desktop/mobile modes)
//   - AutomationsTab body preview dropdown in SettingsPage
//
// Keep these two renderers in sync — if you add a block type here,
// update the backend one too (apps/backend/src/email/email-renderer.ts).

export function renderBlocksToHtml(blocks: EmailBlock[]): string {
  const rows = blocks
    .map((b) => {
      const p = b.props;
      switch (b.type) {
        case 'header': {
          const logo = p.imageUrl ? `<img src="${p.imageUrl}" alt="" style="max-height:56px;max-width:100%;display:inline-block;margin:0 0 ${p.text ? 12 : 0}px" /><br/>` : '';
          const bg = p.bgImageUrl ? `background-image:url('${p.bgImageUrl}');background-size:cover;background-position:center;` : '';
          const title = p.text ? `<h1 style="color:${p.textColor || '#fff'};margin:0;font-size:${p.fontSize}px;font-weight:700">${p.text}</h1>` : '';
          return `<tr><td style="background-color:${p.bgColor};${bg}padding:${p.padding}px 24px;text-align:${p.alignment}">${logo}${title}</td></tr>`;
        }
        case 'text':
          return `<tr><td style="padding:${p.padding}px 24px;text-align:${p.alignment}"><p style="margin:0;font-size:${p.fontSize}px;color:${p.color};line-height:1.6;${p.bold ? 'font-weight:700;' : ''}${p.italic ? 'font-style:italic;' : ''}white-space:pre-wrap">${(p.text || '').replace(/\n/g, '<br>')}</p></td></tr>`;
        case 'image':
          return `<tr><td style="padding:${p.padding}px 24px;text-align:${p.alignment}"><img src="${p.url}" alt="${p.alt}" style="width:${p.width};max-width:100%;border-radius:4px" /></td></tr>`;
        case 'button':
          return `<tr><td style="padding:${p.padding}px 24px;text-align:${p.alignment}"><a href="${p.url}" style="display:inline-block;background:${p.bgColor};color:${p.textColor};padding:10px 24px;border-radius:${p.borderRadius}px;font-size:14px;font-weight:600;text-decoration:none">${p.text}</a></td></tr>`;
        case 'divider':
          return `<tr><td style="padding:${p.padding}px 24px"><hr style="border:none;border-top:${p.thickness}px solid ${p.color};width:${p.width}%" /></td></tr>`;
        case 'spacer':
          return `<tr><td style="height:${p.height}px">&nbsp;</td></tr>`;
        case 'footer':
          return `<tr><td style="background:#F8FAFC;padding:${p.padding}px 24px;text-align:${p.alignment};border-top:1px solid #E2E8F0">${p.imageUrl ? `<img src="${p.imageUrl}" alt="" style="max-height:32px;max-width:100%;display:inline-block;margin:0 0 8px" /><br/>` : ''}<p style="margin:0;font-size:${p.fontSize}px;color:${p.color};line-height:1.5;white-space:pre-wrap">${(p.text || '').replace(/\n/g, '<br>')}</p></td></tr>`;
        default:
          return '';
      }
    })
    .join('');
  return `<table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;margin:0 auto;max-width:100%;font-family:Arial,sans-serif">${rows}</table>`;
}
